// DevHub CUSTOM — the "DevHub Curator" chat widget.
//
// An ADVISORY-ONLY assistant for INTERNAL users. It is mounted globally inside the
// internal hub shell (see Root.tsx) and NEVER on the public/partner portal — partners
// reach the Curator only via MCP.
//
// What it does: a floating launcher (bottom-right) opens a chat panel; the user asks a
// question; we POST it to `{backend.baseUrl}/api/ai/curator/ask` (authenticated); we render
// the answer plus any `sources` as clickable links. The ONLY navigation the widget performs
// is the user clicking a source link — there is no auto-routing, no confirm dialog, and
// crucially NO mutating action of any kind. It shows text + links, nothing else.
//
// Privacy: we derive a coarse page-context LABEL (module / section) from the current route
// ONLY (see ROUTE_CONTEXT below). We never send field values, form data, PII, or DOM content
// — labels only, and we default to sending no context at all.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MarkdownContent } from '@backstage/core-components';
import {
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { curatorMessages } from '../../i18n/curator';
import './CuratorWidget.css';

// ─── Types mirror the backend contract ───
interface CuratorSource {
  n: number;
  title: string;
  url: string;
}

interface CuratorResponse {
  answer: string;
  sources: CuratorSource[];
  refused: boolean;
  reason?: string;
}

interface CuratorContext {
  module?: string;
  section?: string;
}

interface ChatMessage {
  id: number;
  role: 'user' | 'curator';
  text: string;
  sources?: CuratorSource[];
  refused?: boolean;
  /** Transient "thinking…" placeholder, replaced once the answer lands. */
  pending?: boolean;
  /** The request failed — render as an error line, not an answer. */
  errored?: boolean;
}

// ─── Route → coarse context LABELS ───
// A prefix map from route → { module?, section? }. These are static labels only; nothing
// is read from the page/DOM. Longest-prefix wins so `/self-service/...` beats `/`. Default:
// no context (undefined) — we only attach a label when a rule matches.
interface RouteRule {
  prefix: string;
  module?: string;
  section?: string;
}

const ROUTE_CONTEXT: RouteRule[] = [
  // Self Service = the DevSecOps hub (provided modules).
  { prefix: '/self-service', module: 'self-service', section: 'self-service' },
  { prefix: '/devsecops', module: 'devsecops', section: 'self-service' },
  // Docs surfaces.
  { prefix: '/docs-curated', section: 'docs' },
  { prefix: '/docs-overview', section: 'docs' },
  { prefix: '/docs', section: 'docs' },
  // Catalog surfaces.
  { prefix: '/catalog-overview', section: 'catalog' },
  { prefix: '/catalog', section: 'catalog' },
  { prefix: '/api-docs', section: 'catalog' },
  // Other IA sections.
  { prefix: '/resources', section: 'resources' },
  { prefix: '/announcements', section: 'news' },
  { prefix: '/community', section: 'community' },
  { prefix: '/admin/mcp-catalog', module: 'mcp-catalog', section: 'admin' },
  { prefix: '/admin', section: 'admin' },
];

/** Derive a safe context (labels only) from the current path, or undefined. */
function deriveContext(pathname: string): CuratorContext | undefined {
  // Longest matching prefix wins.
  const match = [...ROUTE_CONTEXT]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find(r => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`));
  if (!match) return undefined;
  const ctx: CuratorContext = {};
  if (match.module) ctx.module = match.module;
  if (match.section) ctx.section = match.section;
  return Object.keys(ctx).length ? ctx : undefined;
}

export const CuratorWidget = () => {
  const { t } = useTranslationRef(curatorMessages);
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const location = useLocation();
  const navigate = useNavigate();

  // Open a source without destroying the chat: internal links SPA-navigate (the widget is mounted
  // globally in Root, so it — and the conversation — persist across the route change); external links
  // open in a new tab. Either way the user can read the source and come straight back to ask a follow-up.
  const openSource = useCallback(
    (url: string) => {
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else if (url.startsWith('/')) {
        navigate(url);
      } else if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    [navigate],
  );

  const backendUrl = useMemo(
    () => config.getString('backend.baseUrl'),
    [config],
  );

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const idRef = useRef(0);
  const nextId = () => (idRef.current += 1);

  const panelRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ESC closes the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus the input when the panel opens; on close, return focus to the launcher
  // that opened it (a11y: keyboard focus shouldn't get lost when the dialog closes).
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else if (wasOpenRef.current) {
      launcherRef.current?.focus();
    }
    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Replace the pending bubble with the final authoritative answer + sources (used by both the
  // stream's `done` event and the non-stream fallback).
  const finalize = useCallback((id: number, data: CuratorResponse) => {
    setMessages(prev =>
      prev.map(m =>
        m.id === id
          ? {
              ...m,
              pending: false,
              text: data.answer ?? '',
              sources: Array.isArray(data.sources) ? data.sources : [],
              refused: Boolean(data.refused),
            }
          : m,
      ),
    );
  }, []);

  // Non-streaming fallback: the original one-shot POST. Used if streaming is unavailable or fails
  // before producing an answer, so the widget always degrades to a working request.
  const askOneShot = useCallback(
    async (id: number, body: string) => {
      const res = await fetchApi.fetch(`${backendUrl}/api/ai/curator/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      finalize(id, await res.json());
    },
    [fetchApi, backendUrl, finalize],
  );

  const runQuery = useCallback(async (raw: string) => {
    const query = raw.trim();
    if (!query || busy) return;

    const userMsg: ChatMessage = { id: nextId(), role: 'user', text: query };
    const pendingMsg: ChatMessage = {
      id: nextId(),
      role: 'curator',
      text: t('thinking'),
      pending: true,
    };
    setMessages(prev => [...prev, userMsg, pendingMsg]);
    setInput('');
    setBusy(true);

    // Labels only — never form/DOM/PII content.
    const ctx = deriveContext(location.pathname);
    // Recent conversation so far (for follow-ups) — settled turns only, mapped to the API's roles.
    const history = messages
      .filter(m => !m.pending && !m.errored && m.text)
      .slice(-6)
      .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }));
    const body = JSON.stringify({ query, ...(ctx ? { ctx } : {}), history });

    // Append streamed text to the pending bubble, replacing the "Thinking…" placeholder on first token.
    // The replace-vs-append decision reads the message's OWN `pending` flag inside the updater — NOT an
    // external closure flag: React may run batched updaters after that flag has already flipped, which
    // would leave "Thinking…" prefixed to the whole streamed answer. `m.pending` is only true for the
    // very first token, so the placeholder is cleared exactly once, race-free.
    let streamedAny = false;
    const appendToken = (text: string) => {
      setMessages(prev =>
        prev.map(m =>
          m.id === pendingMsg.id
            ? { ...m, pending: false, text: (m.pending ? '' : m.text) + text }
            : m,
        ),
      );
      streamedAny = true;
    };

    try {
      const res = await fetchApi.fetch(`${backendUrl}/api/ai/curator/ask/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body,
      });
      // No streamable body (or a non-OK status) → fall back to the one-shot endpoint.
      if (!res.ok || !res.body || typeof (res.body as any).getReader !== 'function') {
        await askOneShot(pendingMsg.id, body);
        return;
      }

      // Parse the SSE stream: `event: <type>` + `data: <json>` records, blank-line separated.
      const reader = (res.body as ReadableStream<Uint8Array>).getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let done: CuratorResponse | undefined;

      const handleRecord = (record: string) => {
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of record.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        }
        if (!dataLines.length) return;
        let payload: any;
        try {
          payload = JSON.parse(dataLines.join('\n'));
        } catch {
          return;
        }
        if (event === 'token' && typeof payload.text === 'string') appendToken(payload.text);
        else if (event === 'done') done = payload as CuratorResponse;
      };

      for (;;) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        // eslint-disable-next-line no-cond-assign
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          handleRecord(buffer.slice(0, sep));
          buffer = buffer.slice(sep + 2);
        }
      }
      buffer += decoder.decode();
      if (buffer.trim()) handleRecord(buffer);

      if (done) {
        // The `done` event is authoritative (clean text + sources + refusal state).
        finalize(pendingMsg.id, done);
      } else if (!streamedAny) {
        // Stream produced nothing usable — try the one-shot path before giving up.
        await askOneShot(pendingMsg.id, body);
      }
      // else: tokens streamed but no `done` arrived — keep the streamed text as-is (no sources).
    } catch {
      // Streaming failed. If we already streamed partial text, keep it; otherwise try the one-shot
      // endpoint, and only surface the error line if that fails too.
      try {
        if (!streamedAny) {
          await askOneShot(pendingMsg.id, body);
        }
      } catch {
        setMessages(prev =>
          prev.map(m =>
            m.id === pendingMsg.id
              ? { ...m, pending: false, errored: true, text: t('error') }
              : m,
          ),
        );
      }
    } finally {
      // Ensure the bubble never stays stuck in the pending state.
      setMessages(prev =>
        prev.map(m => (m.id === pendingMsg.id && m.pending ? { ...m, pending: false } : m)),
      );
      setBusy(false);
    }
  }, [busy, t, location.pathname, fetchApi, backendUrl, messages, askOneShot, finalize]);

  const submit = useCallback(() => runQuery(input), [runQuery, input]);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <>
      {/* Floating launcher — bottom-right */}
      <button
        ref={launcherRef}
        type="button"
        className={`devhub-curator-launcher ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={t('launcherLabel')}
        aria-label={t('launcherLabel')}
        aria-expanded={open}
      >
        <odx-icon name="core::chat-agent" size="lg" aria-hidden="true" />
        <span className="devhub-curator-beta devhub-curator-beta--launcher">Beta</span>
      </button>

      {open && (
        <div
          className="devhub-curator-panel"
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={t('header')}
        >
          <div className="devhub-curator-header">
            <span className="devhub-curator-header__avatar" aria-hidden="true">
              <odx-icon name="core::chat-agent" size="md" />
            </span>
            <div className="devhub-curator-header__titles">
              <span className="devhub-curator-header__title-row">
                <span className="devhub-curator-header__title">{t('header')}</span>
                <span className="devhub-curator-beta">Beta</span>
              </span>
              <span className="devhub-curator-header__subtitle">
                {t('subheader')}
              </span>
            </div>
            <button
              type="button"
              className="devhub-curator-header__close"
              onClick={() => setOpen(false)}
              title={t('closeLabel')}
              aria-label={t('closeLabel')}
            >
              <odx-icon className="odx-icon" name="core::close" />
            </button>
          </div>

          <div className="devhub-curator-messages">
            {messages.length === 0 && (
              <div className="devhub-curator-empty">
                <span className="devhub-curator-empty__avatar" aria-hidden="true">
                  <odx-icon name="core::chat-agent" size="lg" />
                </span>
                <p className="devhub-curator-empty__title">{t('emptyTitle')}</p>
                <p className="devhub-curator-empty__text">{t('emptyState')}</p>
                <span className="devhub-curator-empty__examples-label">{t('examplesLabel')}</span>
                <div className="devhub-curator-empty__examples">
                  {[t('example1'), t('example2'), t('example3')].map(ex => (
                    <button
                      key={ex}
                      type="button"
                      className="devhub-curator-example"
                      onClick={() => runQuery(ex)}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map(m => (
              <div
                key={m.id}
                className={`devhub-curator-msg devhub-curator-msg--${m.role} ${
                  m.pending ? 'is-pending' : ''
                } ${m.errored ? 'is-error' : ''} ${
                  m.refused ? 'is-refused' : ''
                }`}
              >
                <div className="devhub-curator-msg__bubble">
                  {m.role === 'curator' && !m.pending ? (
                    // Render the answer's markdown (bold, lists, code, [n] citation markers).
                    <MarkdownContent
                      className="devhub-curator-md"
                      content={m.text}
                      dialect="gfm"
                    />
                  ) : (
                    m.text
                  )}
                </div>
                {m.sources && m.sources.length > 0 && (
                  <div className="devhub-curator-sources">
                    <span className="devhub-curator-sources__label">
                      {t('sources')}
                    </span>
                    <ul className="devhub-curator-sources__list">
                      {m.sources.map(s => (
                        <li key={`${m.id}-${s.n}`}>
                          {/* The user clicks to navigate — the ONLY navigation. Internal links
                              SPA-navigate so the chat survives; external open in a new tab. */}
                          <a
                            className="devhub-curator-source-link"
                            href={s.url}
                            onClick={e => {
                              e.preventDefault();
                              openSource(s.url);
                            }}
                          >
                            <odx-icon
                              className="odx-icon"
                              name="core::link-external"
                              size="sm"
                            />
                            {s.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="devhub-curator-input">
            <input
              ref={inputRef}
              type="text"
              className="devhub-curator-input__field"
              placeholder={t('placeholder')}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onInputKeyDown}
              aria-label={t('placeholder')}
            />
            <button
              type="button"
              className="devhub-curator-input__send"
              onClick={submit}
              disabled={busy || input.trim().length === 0}
              title={t('send')}
              aria-label={t('sendLabel')}
            >
              <odx-icon className="odx-icon" name="core::arrow-right" />
            </button>
          </div>

          <div className="devhub-curator-disclaimer">{t('disclaimer')}</div>
        </div>
      )}
    </>
  );
};
